// Cruscotto operativo dei turni dei servizi fiduciari.
import {
    Activity,
    AlertTriangle,
    Archive,
    Building2,
    Camera,
    CheckCircle,
    CheckSquare,
    Clock,
    Handshake,
    Info,
    Loader2,
    MapPin,
    Plus,
    Play, Receipt, Send, Shield,
    StopCircle, X, XCircle, Pencil, FileText, RotateCcw,
} from "lucide-react";
import SessionTimer from "../SessionTimer";
import { RelazioneGiornalieraService, ServizioFiduciarioService } from "../../services/apiService";
import { useToast } from "../ToastContext";
import {
    hasOrariDifformiTurno,
    getOrariDifformiMinutiTurno,
    useSogliaOrariDifformiTurni,
    formatMinutiBreve,
} from "../../utils/incaricoUtils";
import { useState, useEffect, useMemo } from "react";
import { NoteModal } from "./NoteModal";
import NumberInput from "../NumberInput";
import AutoAziendaleSelector from "../AutoAziendaleSelector";
import { parseDecimal, localIsoDate } from "../../utils/utils";
import { safeAction } from "../../utils/safeAction";
import { canResumeTurnoAfterPause } from "../../utils/turnoStato";

interface SpesaRow {
    tipo: string;
    importo: string;
    note: string;
    anticipatoAzienda: boolean;
    file: File | null;
}

const TurnoOperativoDashboard: React.FC<{ turno: any, isStorico: boolean, onBack: () => void, onRefresh: () => void }> = ({ turno, isStorico, onBack, onRefresh }) => {
    const { addToast } = useToast();
    const [actionLoading, setActionLoading] = useState(false);

    // ── Stato form report fine turno (km, ore extra, spese, note) ────────────
    const [kmFatti, setKmFatti] = useState('');
    const [oreExtra, setOreExtra] = useState('');
    const [autoAziendale, setAutoAziendale] = useState(false);
    const [noteFineTurno, setNoteFineTurno] = useState('');
    const [listaSpese, setListaSpese] = useState<SpesaRow[]>([]);
    const [isSavingReport, setIsSavingReport] = useState(false);

    // Come in ReportTab: il collaboratore può riaprire un report già trasmesso
    // (DA_REVISIONARE o APPROVATO). /modifica lo rimanda in revisione e non
    // tocca spese e foto.
    const [userInitiatedEdit, setUserInitiatedEdit] = useState(false);
    const reportData = (turno as any).report;
    const isDaModificare = reportData?.stato === 'DA_MODIFICARE';
    const isEditingExisting = userInitiatedEdit || isDaModificare;
    const canCollaboratorEdit = !!reportData
        && (reportData.stato === 'APPROVATO' || reportData.stato === 'DA_REVISIONARE');

    const getPos = (): Promise<GeolocationPosition> => {
        return new Promise((res, rej) => navigator.geolocation.getCurrentPosition(res, rej, { enableHighAccuracy: true, timeout: 5000 }));
    };

    // Start/stop con foto alla postazione come prova di presenza. L'API la
    // considera facoltativa: se l'utente annulla si procede senza foto.
    // Sul 422 (foto senza EXIF o non recente) si mostra il messaggio del BE.
    const extractExifError = (err: any): string | null => {
        if (err?.response?.status !== 422) return null;
        const data = err?.response?.data;
        const msg = data?.message || data?.error;
        return typeof msg === 'string' && msg.length > 0
            ? msg
            : "Foto non valida: devi scattarla con la fotocamera adesso, non sceglierla dalla galleria.";
    };

    const handleStartWithPhoto = async (e: React.ChangeEvent<HTMLInputElement>) => {
        if (isStorico) return;
        const file = e.target.files?.[0];
        e.target.value = '';
        setActionLoading(true);
        try {
            let lat = 0, lon = 0;
            try { const p = await getPos(); lat = p.coords.latitude; lon = p.coords.longitude; } catch(_){ /* no GPS */ }
            await safeAction(
                { targetAction: 'TURNO_START', entityType: 'turno', entityId: turno.id, payload: { lat, lon, hasPhoto: !!file } },
                () => ServizioFiduciarioService.startTurnoWithPhoto(turno.id, lat, lon, file || undefined),
            );
            addToast("Turno iniziato. Buon lavoro!", "success");
            onRefresh();
        } catch (err) {
            const exifMsg = extractExifError(err);
            addToast(exifMsg ?? "Impossibile avviare il turno sul server.", "error");
        } finally {
            setActionLoading(false);
        }
    };

    const handleStopWithPhoto = async (e: React.ChangeEvent<HTMLInputElement>) => {
        if (isStorico) return;
        const file = e.target.files?.[0];
        e.target.value = '';
        if (!window.confirm("Vuoi terminare il turno? Subito dopo potrai inviare km e spese.")) return;
        setActionLoading(true);
        try {
            let lat = 0, lon = 0;
            try { const p = await getPos(); lat = p.coords.latitude; lon = p.coords.longitude; } catch(_){ /* no GPS */ }
            await safeAction(
                { targetAction: 'TURNO_STOP', entityType: 'turno', entityId: turno.id, payload: { lat, lon, hasPhoto: !!file } },
                () => ServizioFiduciarioService.stopTurnoWithPhoto(turno.id, lat, lon, file || undefined),
            );
            addToast("Turno terminato. Completa il report fine turno.", "success");
            onRefresh();
        } catch (err) {
            const exifMsg = extractExifError(err);
            addToast(exifMsg ?? "Errore di chiusura turno sul server.", "error");
        } finally {
            setActionLoading(false);
        }
    };

    // Note operative durante il turno (es. tentato furto), distinte dalla nota
    // di fine turno del report.
    const [noteList, setNoteList] = useState<any[]>(turno.note || []);
    const [isNoteModalOpen, setIsNoteModalOpen] = useState(false);
    const [noteText, setNoteText] = useState('');
    const [savingNote, setSavingNote] = useState(false);

    useEffect(() => {
        setNoteList(turno.note || []);
    }, [turno?.id, turno?.note?.length]);

    const handleSaveNote = async () => {
        if (!noteText.trim()) return;
        setSavingNote(true);
        try {
            let lat: number | undefined, lon: number | undefined;
            try { const p = await getPos(); lat = p.coords.latitude; lon = p.coords.longitude; } catch (_) { /* GPS opzionale */ }
            const res = await ServizioFiduciarioService.addNotaTurno(turno.id, noteText.trim(), lat, lon);
            const newNote = (res as any).data?.data ?? (res as any).data ?? null;
            if (newNote) {
                setNoteList(prev => [newNote, ...prev]);
            } else {
                // fallback: ricarica tutte le note del turno
                try {
                    const all = await ServizioFiduciarioService.getNoteTurno(turno.id);
                    setNoteList((all as any).data ?? []);
                } catch (_) {}
            }
            addToast('Nota operativa registrata', 'success');
            setNoteText('');
            setIsNoteModalOpen(false);
        } catch (_) {
            addToast("Errore nel salvataggio della nota", 'error');
        } finally {
            setSavingNote(false);
        }
    };

    const addSpesaRow = () =>
        setListaSpese(prev => [...prev, { tipo: '', importo: '', note: '', anticipatoAzienda: false, file: null }]);
    const removeSpesaRow = (i: number) => setListaSpese(prev => prev.filter((_, idx) => idx !== i));
    const updateSpesa = (i: number, field: keyof SpesaRow, value: any) =>
        setListaSpese(prev => { const n = [...prev]; (n[i] as any)[field] = value; return n; });

    // Preferisce la somma delle sessioni calcolata dal BE, che esclude le pause.
    // `effettivoFine - effettivoInizio` resta come fallback per i turni vecchi.
    const oreGps = useMemo(() => {
        if (typeof (turno as any).oreEffettiveGpsSommate === 'number') {
            return (turno as any).oreEffettiveGpsSommate;
        }
        if (!turno.effettivoInizio || !turno.effettivoFine) return 0;
        const start = new Date(turno.effettivoInizio).getTime();
        const end = new Date(turno.effettivoFine).getTime();
        if (!isFinite(start) || !isFinite(end) || end <= start) return 0;
        return Math.round(((end - start) / 3_600_000) * 10) / 10;
    }, [(turno as any).oreEffettiveGpsSommate, turno.effettivoInizio, turno.effettivoFine]);

    // Orari effettivi per il riepilogo: una riga per ogni ciclo Start→Stop
    // e totale netto senza pause.
    const fmtOra = (iso?: string | null) =>
        iso ? new Date(iso).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' }) : '';
    const fmtDurata = (startIso?: string | null, endIso?: string | null) => {
        if (!startIso || !endIso) return '';
        const min = Math.max(0, Math.round((new Date(endIso).getTime() - new Date(startIso).getTime()) / 60000));
        return min >= 60 ? `${Math.floor(min / 60)}h ${String(min % 60).padStart(2, '0')}m` : `${min}m`;
    };
    const sessioniEffettive: any[] = ((turno as any).sessioni || []).filter((s: any) => s?.startAt);
    const hasEffettivo = !!turno.effettivoInizio || sessioniEffettive.length > 0;

    // In modifica precompila il form con i valori della relazione esistente.
    useEffect(() => {
        if (!isEditingExisting || !reportData) return;
        setNoteFineTurno(reportData.testoRelazione || '');
        setOreExtra(reportData.oreExtra != null ? String(reportData.oreExtra) : '');
        setKmFatti(reportData.kmPercorsi != null ? String(reportData.kmPercorsi) : '');
        setAutoAziendale(!!reportData.autoAziendale);
    }, [reportData?.id, isEditingExisting]);

    const handleSendReport = async () => {
        // Re-invio: solo i campi della relazione, spese e foto invariate.
        if (isEditingExisting && reportData?.id) {
            setIsSavingReport(true);
            try {
                await RelazioneGiornalieraService.modifica(reportData.id, {
                    testoRelazione: noteFineTurno || '',
                    oreExtra: parseDecimal(oreExtra),
                    kmPercorsi: parseDecimal(kmFatti),
                    autoAziendale,
                });
                addToast('Report aggiornato e re-inviato al titolare', 'success');
                setUserInitiatedEdit(false);
                onRefresh();
            } catch {
                addToast('Errore aggiornamento report', 'error');
            } finally {
                setIsSavingReport(false);
            }
            return;
        }

        for (let i = 0; i < listaSpese.length; i++) {
            const s = listaSpese[i];
            if (!s.tipo) { addToast(`Spesa #${i + 1}: Seleziona la Categoria!`, 'error'); return; }
            if (parseDecimal(s.importo) <= 0) { addToast(`Spesa #${i + 1}: Inserisci un Importo valido!`, 'error'); return; }
        }

        setIsSavingReport(true);
        try {
            const dataTurno = turno.dataTurno || turno.effettivoInizio?.split('T')[0] || localIsoDate();
            const hInizio = turno.effettivoInizio ? new Date(turno.effettivoInizio).toTimeString().slice(0, 5) : (turno.oraInizio || '');
            const hFine = turno.effettivoFine ? new Date(turno.effettivoFine).toTimeString().slice(0, 5) : (turno.oraFine || '');

            const formData = new FormData();
            const payload: any = {
                turnoId: turno.id,
                data: dataTurno,
                relazione: noteFineTurno || '',
                orarioInizioIndagine: hInizio,
                orarioFineIndagine: hFine,
                oreGps,
                oreExtra: parseDecimal(oreExtra),
                km: parseDecimal(kmFatti),
                autoAziendale,
                spese: listaSpese.map(s => ({
                    tipo: s.tipo,
                    importo: parseDecimal(s.importo),
                    note: s.note,
                    anticipatoAzienda: s.anticipatoAzienda,
                })),
            };
            formData.append('data', JSON.stringify(payload));
            listaSpese.forEach((spesa, idx) => { if (spesa.file) formData.append(`file_${idx}`, spesa.file); });

            await safeAction(
                { targetAction: 'REPORT_INVIATO', entityType: 'turno', entityId: turno.id, payload: { km: parseDecimal(kmFatti), numSpese: listaSpese.length } },
                () => RelazioneGiornalieraService.create(formData),
            );
            addToast('Report e spese trasmessi con successo', 'success');
            setNoteFineTurno(''); setListaSpese([]); setKmFatti(''); setOreExtra(''); setAutoAziendale(false);
            onRefresh();
            onBack();
        } catch {
            addToast('Errore di trasmissione report', 'error');
        } finally {
            setIsSavingReport(false);
        }
    };

    const isActive = !!turno.effettivoInizio && !turno.effettivoFine;
    const reportGiaTrasmesso = !!turno.reportTrasmesso;
    // "Concluso" solo dopo l'invio della relazione: prima il turno è in pausa
    // e si può riprendere.
    const isCompleted = !!turno.effettivoInizio && !!turno.effettivoFine && reportGiaTrasmesso;
    const isInPausa = !!turno.effettivoInizio && !!turno.effettivoFine && !reportGiaTrasmesso;
    // Un turno futuro si può consultare ma non avviare prima della sua data
    // (confronto tra date locali).
    const todayIso = (() => {
        const d = new Date();
        const pad = (n: number) => String(n).padStart(2, '0');
        return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
    })();
    const dataTurnoStr = typeof turno.dataTurno === 'string' ? turno.dataTurno.slice(0, 10) : '';
    const isFuturo = !!dataTurnoStr && dataTurnoStr > todayIso && !turno.effettivoInizio;

    // "Riprendi" solo nel giorno del turno (o il successivo se notturno);
    // poi resta solo il report. Regola in utils/turnoStato.
    const canResumeAfterPause = canResumeTurnoAfterPause(turno);

    // Rifiutato o revocato: stato terminale, solo consultazione.
    const isRifiutato = turno.statoAccettazione === 'RIFIUTATO';
    const isRevocatoTurno = !!turno.dataRevoca;
    const isReadOnlyTerminal = isRifiutato || isRevocatoTurno;

    // Come per gli incarichi: finché è ASSEGNATO va accettato prima dello Start.
    const isDaAccettare = turno.statoAccettazione === 'ASSEGNATO' && !isStorico && !isReadOnlyTerminal;
    // Accettato ma non avviato: si può ancora rifiutare (se non è già passato).
    const isAccettatoNonAvviato = turno.statoAccettazione === 'ACCETTATO'
        && !turno.effettivoInizio && !isStorico && !isReadOnlyTerminal;

    // Allerta scostamento orari: solo a turno iniziato e oltre la soglia
    // configurata per i fiduciari.
    const sogliaOrariTurniMin = useSogliaOrariDifformiTurni();
    const showOrariDifformiTurno = hasOrariDifformiTurno(turno, sogliaOrariTurniMin);
    const minutiDifformiTurno = showOrariDifformiTurno ? getOrariDifformiMinutiTurno(turno) : null;
    const [rifiutaModalOpen, setRifiutaModalOpen] = useState(false);
    const [motivoRifiuto, setMotivoRifiuto] = useState('');

    const handleAccetta = async () => {
        if (actionLoading) return;
        setActionLoading(true);
        try {
            await ServizioFiduciarioService.accettaTurno(turno.id);
            addToast('Turno accettato. Ora puoi avviarlo quando sei in postazione.', 'success');
            onRefresh();
        } catch (err: any) {
            const msg = err?.response?.data?.message || err?.response?.data?.error || 'Errore durante l’accettazione.';
            addToast(msg, 'error');
        } finally {
            setActionLoading(false);
        }
    };

    const handleRifiuta = async () => {
        if (actionLoading) return;
        const motivo = (motivoRifiuto || '').trim();
        if (!motivo) {
            addToast('Indica un motivo per il rifiuto.', 'error');
            return;
        }
        setActionLoading(true);
        try {
            await ServizioFiduciarioService.rifiutaTurno(turno.id, motivo);
            addToast('Turno rifiutato. L’amministratore verrà notificato.', 'success');
            setRifiutaModalOpen(false);
            setMotivoRifiuto('');
            onRefresh();
        } catch (err: any) {
            const msg = err?.response?.data?.message || err?.response?.data?.error || 'Errore durante il rifiuto.';
            addToast(msg, 'error');
        } finally {
            setActionLoading(false);
        }
    };

    return (
        <div className="max-w-3xl mx-auto bg-slate-50 min-h-screen relative flex flex-col">
            <div className="sticky top-0 z-40 bg-white/80 backdrop-blur-md border-b border-slate-200 px-4 py-3 flex items-center justify-between">
                <button onClick={onBack} className="p-2 -ml-2 text-slate-500 hover:bg-slate-100 rounded-full transition-colors"><X size={22}/></button>
                <div className="text-center flex-1 px-4 truncate">
                    <h2 className="text-sm font-semibold text-slate-900 flex items-center justify-center gap-1.5"><Shield size={16} className="text-emerald-600"/> Servizio </h2>
                </div>
                <div className="w-10"></div>
            </div>

            <div className="p-5 sm:p-8 space-y-6 flex-1">
                {isStorico && (
                    <div className="bg-amber-50 border border-amber-200 text-amber-700 px-4 py-3 rounded-xl text-sm font-semibold flex items-center gap-2">
                        <Archive size={18}/> Archivio Storico: Questo turno è passato e non può essere modificato.
                    </div>
                )}
                {/* Banner read-only per turni rifiutati o revocati: solo consultazione. */}
                {isRifiutato && (
                    <div className="bg-rose-50 border border-rose-200 text-rose-700 px-4 py-3 rounded-xl text-sm font-semibold flex items-start gap-2">
                        <X size={18} className="mt-0.5 shrink-0"/>
                        <div>
                            <div className="font-bold">Turno rifiutato</div>
                            {turno.motivoRifiuto && (
                                <div className="text-rose-600 font-normal mt-0.5">Motivo: {turno.motivoRifiuto}</div>
                            )}
                            <div className="text-rose-600 font-normal text-xs mt-1">Solo consultazione: nessuna operazione è disponibile.</div>
                        </div>
                    </div>
                )}
                {isRevocatoTurno && (
                    <div className="bg-rose-50 border border-rose-200 text-rose-700 px-4 py-3 rounded-xl text-sm font-semibold flex items-start gap-2">
                        <AlertTriangle size={18} className="mt-0.5 shrink-0"/>
                        <div>
                            <div className="font-bold">Turno revocato dal titolare</div>
                            {turno.dataRevoca && (
                                <div className="text-rose-600 font-normal text-xs mt-1">
                                    Revocato il {new Date(turno.dataRevoca).toLocaleString('it-IT')}
                                </div>
                            )}
                            <div className="text-rose-600 font-normal text-xs mt-1">Solo consultazione: nessuna operazione è disponibile.</div>
                        </div>
                    </div>
                )}

                {/* Scostamento orari oltre soglia: solo segnalazione */}
                {showOrariDifformiTurno && !isDaAccettare && !isReadOnlyTerminal && (
                    <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 flex items-start gap-3">
                        <AlertTriangle size={20} className="text-red-500 shrink-0 mt-0.5"/>
                        <div className="flex-1">
                            <div className="text-sm font-black text-amber-900 tracking-wide uppercase mb-1">
                                Orari difformi · {formatMinutiBreve(minutiDifformiTurno)}
                            </div>
                            <div className="text-xs text-amber-800 leading-relaxed">
                                Lo scostamento tra orario pianificato e orario reale supera la soglia impostata dall'agenzia. Il titolare ne è informato.
                            </div>
                        </div>
                    </div>
                )}

                {/* Turno da accettare o rifiutare: fino ad allora lo Start è disabilitato. */}
                {isDaAccettare && (
                    <div className="bg-gradient-to-br from-amber-50 to-orange-50 border-2 border-amber-300 rounded-[2rem] p-6 sm:p-8 shadow-xl relative overflow-hidden">
                        <div className="relative z-10 flex flex-col items-center text-center">
                            <div className="p-4 bg-white rounded-full shadow-md mb-4">
                                <Handshake size={36} className="text-amber-600"/>
                            </div>
                            <h3 className="text-xl font-black text-amber-900 mb-2">Turno da Accettare</h3>
                            <p className="text-sm text-amber-800/80 leading-relaxed max-w-md mb-5">
                                Conferma di prendere in carico questo turno per poterlo avviare quando sei in postazione.
                                In alternativa puoi rifiutarlo: l’amministratore lo riassegnerà ad un altro collaboratore.
                            </p>
                            <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
                                <button
                                    onClick={handleAccetta}
                                    disabled={actionLoading}
                                    className="bg-amber-600 hover:bg-amber-700 disabled:opacity-50 text-white py-3 px-8 rounded-xl font-bold text-base shadow-lg flex items-center justify-center gap-3 transition-colors"
                                >
                                    {actionLoading ? <Loader2 size={20} className="animate-spin"/> : <Handshake size={20}/>}
                                    {actionLoading ? 'Operazione...' : 'Accetta Turno'}
                                </button>
                                <button
                                    onClick={() => { setMotivoRifiuto(''); setRifiutaModalOpen(true); }}
                                    disabled={actionLoading}
                                    className="bg-white hover:bg-rose-50 text-rose-700 border-2 border-rose-200 hover:border-rose-400 py-3 px-8 rounded-xl font-bold text-base shadow flex items-center justify-center gap-3 transition-colors disabled:opacity-50"
                                >
                                    <XCircle size={20}/>
                                    Rifiuta
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Modal conferma rifiuto turno */}
                {rifiutaModalOpen && (
                    <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4"
                         onClick={() => !actionLoading && setRifiutaModalOpen(false)}>
                        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden"
                             onClick={e => e.stopPropagation()}>
                            <div className="p-5 border-b border-slate-100">
                                <h3 className="font-bold text-slate-900 text-lg">Rifiuta turno</h3>
                                <p className="text-xs text-slate-500 mt-1">Spiega brevemente il motivo: sarà visibile al titolare.</p>
                            </div>
                            <div className="p-5">
                                <textarea
                                    value={motivoRifiuto}
                                    onChange={e => setMotivoRifiuto(e.target.value)}
                                    placeholder="Es. malattia, impegno improvviso, distanza eccessiva..."
                                    rows={4}
                                    className="w-full border border-slate-300 rounded-lg p-3 text-sm focus:outline-none focus:border-rose-400"
                                />
                            </div>
                            <div className="p-4 bg-slate-50 flex items-center justify-end gap-2">
                                <button
                                    onClick={() => !actionLoading && setRifiutaModalOpen(false)}
                                    disabled={actionLoading}
                                    className="px-4 py-2 text-sm text-slate-700 hover:bg-slate-200 rounded-lg disabled:opacity-50"
                                >
                                    Annulla
                                </button>
                                <button
                                    onClick={handleRifiuta}
                                    disabled={actionLoading || !motivoRifiuto.trim()}
                                    className="px-4 py-2 bg-rose-600 hover:bg-rose-700 disabled:opacity-50 text-white text-sm font-semibold rounded-lg flex items-center gap-1.5"
                                >
                                    {actionLoading ? <Loader2 size={16} className="animate-spin"/> : <XCircle size={16}/>}
                                    Conferma rifiuto
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                <div className="bg-white border border-slate-200 rounded-3xl overflow-hidden shadow-sm">
                    <div className="bg-emerald-600 px-6 py-5 text-white">
                        <div className="flex items-center gap-2 text-emerald-200 font-bold mb-1 text-xs uppercase tracking-widest">
                            <Building2 size={16}/> {turno.servizio?.cliente || turno.servizio?.clientName || 'Cliente'}
                        </div>
                        <h2 className="text-2xl font-bold leading-tight">{turno.servizio?.nome || 'Postazione Operativa'}</h2>
                    </div>
                    <div className="p-6 space-y-5">
                        <div className="flex items-start gap-3">
                            <MapPin className="text-emerald-500 mt-0.5" size={20}/>
                            <div>
                                <span className="block text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-0.5">Indirizzo Sede Servizio</span>
                                <span className="text-sm font-semibold text-slate-800">{turno.servizio?.indirizzo || 'N/D'}</span>
                            </div>
                        </div>
                        <div className="flex items-start gap-3">
                            <Clock className="text-emerald-500 mt-0.5" size={20}/>
                            <div>
                                <span className="block text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-0.5">Orario Pianificato</span>
                                <span className="text-sm font-semibold text-slate-800">{turno.oraInizio} - {turno.oraFine}</span>
                            </div>
                        </div>
                    </div>
                </div>

                {!isReadOnlyTerminal && (
                <div className="bg-slate-900 rounded-[2rem] p-6 sm:p-10 text-white shadow-xl relative overflow-hidden">
                    <div className="absolute top-0 right-0 p-24 bg-emerald-500/20 blur-3xl rounded-full pointer-events-none"/>

                    <div className="relative z-10 flex flex-col items-center text-center">
                        <div className="flex items-center gap-2 text-slate-400 text-xs font-semibold uppercase tracking-widest mb-8">
                            <Activity size={16}/> Inizia il lavoro
                        </div>

                        {isCompleted ? (
                            <div className="bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 p-6 rounded-2xl w-full">
                                <CheckSquare size={32} className="mx-auto mb-3 opacity-80"/>
                                <h3 className="font-bold text-lg">Turno Concluso</h3>
                                <p className="text-sm opacity-80 mt-1">La sessione è stata registrata correttamente.</p>
                            </div>
                        ) : isFuturo ? (
                            <div className="bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 p-6 rounded-2xl w-full">
                                <Clock size={32} className="mx-auto mb-3 opacity-80"/>
                                <h3 className="font-bold text-lg">Turno non ancora iniziato</h3>
                                <p className="text-sm opacity-80 mt-1">
                                    Potrai avviarlo a partire dal {new Date(dataTurnoStr).toLocaleDateString('it-IT', { weekday: 'long', day: '2-digit', month: 'long' })}.
                                </p>
                            </div>
                        ) : isStorico ? (
                            <div className="bg-rose-500/10 border border-rose-500/30 text-rose-400 p-6 rounded-2xl w-full">
                                <AlertTriangle size={32} className="mx-auto mb-3 opacity-80"/>
                                <h3 className="font-bold text-lg">Turno Scaduto / Non Effettuato</h3>
                                <p className="text-sm opacity-80 mt-1">
                                    {turno.effettivoFine
                                        ? 'Turno chiuso ma report non inviato.'
                                        : 'La data del turno è passata senza registrazioni.'}
                                </p>
                            </div>
                        ) : isActive ? (
                            <div className="w-full flex flex-col items-center space-y-8">
                                {/* Il timer parte dall'ultima sessione aperta, così dopo un Riprendi
                                    non conta la pausa. */}
                                <SessionTimer startTimestamp={turno.sessioneCorrenteInizio || turno.effettivoInizio} />
                                {/* Foto alla postazione allo stop, come prova */}
                                <label className="w-full sm:w-2/3 bg-rose-600 hover:bg-rose-500 text-white py-5 rounded-2xl font-bold text-xl shadow-lg shadow-rose-900/50 transition-all active:scale-[0.98] disabled:opacity-50 flex items-center justify-center gap-3 cursor-pointer">
                                    {actionLoading ? <Loader2 className="animate-spin" size={24}/> : <StopCircle size={28}/>}
                                    {actionLoading ? 'Registrazione...' : 'Scatta Foto e Termina'}
                                    <input type="file" accept="image/*" capture="environment" className="hidden"
                                           onChange={handleStopWithPhoto} disabled={actionLoading} />
                                </label>
                            </div>
                        ) : (
                            <div className="w-full flex flex-col items-center space-y-6">
                                <p className="text-sm text-slate-400 max-w-sm">
                                    {isDaAccettare
                                        ? 'Devi prima accettare il turno (banner in alto) per poter avviare la sessione.'
                                        : isInPausa
                                            ? (canResumeAfterPause
                                                ? 'Turno in pausa. Scatta una foto e riprendi per ricominciare il timer.'
                                                : 'Turno in pausa dal giorno precedente: non è più possibile riaprire una nuova sessione, invia il report per chiudere definitivamente.')
                                            : 'Assicurati di essere fisicamente presso la postazione.'}
                                </p>
                                {(!isInPausa || canResumeAfterPause) && (
                                    <label className={`w-full sm:w-2/3 ${isDaAccettare ? 'bg-slate-500 cursor-not-allowed' : 'bg-emerald-600 hover:bg-emerald-500 cursor-pointer'} text-white py-5 rounded-2xl font-bold text-xl shadow-lg shadow-emerald-900/50 transition-all active:scale-[0.98] disabled:opacity-50 flex items-center justify-center gap-3`}>
                                        {actionLoading ? <Loader2 className="animate-spin" size={24}/> : <Camera size={28}/>}
                                        {actionLoading
                                            ? (isInPausa ? 'Ripresa turno...' : 'Inizio incarico...')
                                            : (isInPausa ? 'Scatta Foto e Riprendi' : 'Scatta Foto e Inizia')}
                                        <input type="file" accept="image/*" capture="environment" className="hidden"
                                               onChange={handleStartWithPhoto} disabled={actionLoading || isDaAccettare} />
                                    </label>
                                )}
                            </div>
                        )}
                    </div>
                </div>
                )}

                {/* Rifiuto ancora possibile finché non avviato */}
                {isAccettatoNonAvviato && (
                    <div className="flex justify-center">
                        <button
                            onClick={() => { setMotivoRifiuto(''); setRifiutaModalOpen(true); }}
                            className="text-xs font-bold text-rose-600 hover:text-rose-700 underline underline-offset-2"
                        >Rifiuta turno</button>
                    </div>
                )}

                {/* Note operative del turno, visibili anche in storico (sola lettura). */}
                {(isActive || isCompleted || noteList.length > 0) && (
                    <div className="bg-white border border-slate-200 rounded-3xl shadow-sm overflow-hidden">
                        <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between bg-slate-50/60">
                            <div className="flex items-center gap-2.5">
                                <Pencil size={16} className="text-amber-500"/>
                                <h3 className="text-sm font-bold text-slate-800">Note Operative</h3>
                                {noteList.length > 0 && (
                                    <span className="text-[10px] font-bold text-slate-500 bg-slate-100 px-1.5 py-0.5 rounded">
                                        {noteList.length}
                                    </span>
                                )}
                            </div>
                            {isActive && !isStorico && !isReadOnlyTerminal && (
                                <button
                                    onClick={() => { setNoteText(''); setIsNoteModalOpen(true); }}
                                    className="text-xs font-bold text-amber-700 hover:bg-amber-100 px-3 py-1.5 rounded-md border border-amber-200 flex items-center gap-1.5"
                                >
                                    <Plus size={13}/> Nuova Nota
                                </button>
                            )}
                        </div>
                        <div className="p-4">
                            {noteList.length === 0 ? (
                                <p className="text-xs text-slate-400 italic text-center py-3">
                                    Nessuna nota operativa registrata.
                                    {isActive && ' Usa "Nuova Nota" per documentare eventi rilevanti (es. tentato furto, aggressione).'}
                                </p>
                            ) : (
                                <ul className="space-y-2">
                                    {noteList.map((n: any) => (
                                        <li key={n.id} className="bg-amber-50/60 border border-amber-100 rounded-lg p-3 flex items-start gap-3">
                                            <FileText size={14} className="text-amber-500 shrink-0 mt-0.5"/>
                                            <div className="flex-1 min-w-0">
                                                <p className="text-sm text-slate-700 whitespace-pre-wrap break-words">{n.text}</p>
                                                <div className="flex items-center gap-3 mt-1 text-[10px] font-bold text-slate-400">
                                                    <span>{n.createdAt ? new Date(n.createdAt).toLocaleString('it-IT', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : ''}</span>
                                                    {n.authorName && <span>· {n.authorName}</span>}
                                                </div>
                                            </div>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    </div>
                )}

                {/* ── REPORT FINE TURNO ─────────────────────────────────────── */}
                {reportGiaTrasmesso && !isEditingExisting && (
                    <div className="bg-emerald-50 border border-emerald-200 rounded-3xl p-8 text-center space-y-4 shadow-sm">
                        <CheckCircle size={48} className="text-emerald-500 mx-auto" />
                        <h3 className="text-xl font-bold text-emerald-900">Report Già Trasmesso</h3>
                        <p className="text-sm font-medium text-emerald-700">Hai inviato km, spese e note relative a questo turno.</p>
                        {turno.reportStato && (
                            <span className="inline-block bg-white px-4 py-1.5 rounded-full text-[10px] font-bold text-emerald-800 uppercase tracking-widest border border-emerald-200 shadow-sm">
                                Stato: {turno.reportStato}
                            </span>
                        )}
                        {canCollaboratorEdit && !isReadOnlyTerminal && !isStorico ? (
                            <div className="pt-2 space-y-2">
                                <button
                                    type="button"
                                    onClick={() => setUserInitiatedEdit(true)}
                                    className="inline-flex items-center gap-2 bg-white hover:bg-emerald-50 border-2 border-emerald-300 text-emerald-800 font-bold text-sm px-5 py-2.5 rounded-xl shadow-sm transition-colors"
                                >
                                    <RotateCcw size={16} /> Modifica relazione
                                </button>
                                <p className="text-[11px] text-emerald-600/80 leading-snug max-w-md mx-auto">
                                    Puoi correggere il report anche dopo l'invio. Al salvataggio tornerà in attesa di revisione del titolare. Le spese e le foto restano invariati.
                                </p>
                            </div>
                        ) : (
                            <p className="text-xs text-emerald-600 mt-4 opacity-80">Eventuali modifiche vanno richieste all'amministrazione.</p>
                        )}
                    </div>
                )}

                {/* Il form si abilita dopo almeno uno Start+Stop; inviata la relazione
                    il BE blocca nuovi Start/Stop. In modifica è aperto anche dopo l'invio. */}
                {(isInPausa || isEditingExisting) && !isStorico && !isReadOnlyTerminal && (
                    <div className="bg-white border border-slate-200 rounded-3xl shadow-sm p-6 sm:p-8 space-y-8">
                        <div className="flex items-center gap-3 pb-3 border-b border-slate-200">
                            <Receipt size={22} className="text-emerald-600"/>
                            <h3 className="text-lg font-semibold text-slate-900">Report Fine Turno</h3>
                        </div>

                        {/* Richiesta modifiche dal titolare */}
                        {isDaModificare && (
                            <div className="bg-amber-50 border-2 border-amber-300 text-amber-900 p-4 rounded-2xl flex gap-3 text-sm font-medium shadow-sm">
                                <AlertTriangle size={26} className="shrink-0 text-amber-600 mt-0.5" />
                                <div className="min-w-0">
                                    <p className="font-black uppercase tracking-wider text-xs mb-1.5 flex items-center gap-2">
                                        Richiesta modifiche dal titolare
                                        <span className="inline-block bg-amber-200 text-amber-900 px-2 py-0.5 rounded-full text-[9px] uppercase font-black tracking-wider">Da Modificare</span>
                                    </p>
                                    {reportData?.motivoRichiestaModifica ? (
                                        <p className="leading-relaxed whitespace-pre-wrap text-amber-900/90">
                                            "{reportData.motivoRichiestaModifica}"
                                        </p>
                                    ) : (
                                        <p className="leading-relaxed text-amber-900/80 italic">Nessun motivo specifico fornito.</p>
                                    )}
                                    <p className="text-[11px] text-amber-700/80 mt-2">Modifica i campi qui sotto e re-invia. Le spese e le foto restano invariati.</p>
                                </div>
                            </div>
                        )}

                        {/* Modifica avviata dal collaboratore */}
                        {userInitiatedEdit && !isDaModificare && (
                            <div className="bg-blue-50 border-2 border-blue-300 text-blue-900 p-4 rounded-2xl flex gap-3 text-sm font-medium shadow-sm">
                                <RotateCcw size={26} className="shrink-0 text-blue-600 mt-0.5" />
                                <div className="min-w-0 flex-1">
                                    <p className="font-black uppercase tracking-wider text-xs mb-1.5">Stai modificando una relazione già trasmessa</p>
                                    <p className="leading-relaxed text-blue-900/85">
                                        Correggi km, ore extra o testo e clicca "Re-invia" per ri-mandarla in revisione. Le spese e le foto restano invariati.
                                    </p>
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setUserInitiatedEdit(false);
                                            setNoteFineTurno('');
                                            setOreExtra('');
                                            setKmFatti('');
                                            setAutoAziendale(false);
                                        }}
                                        className="mt-2 text-[11px] font-bold text-blue-700 hover:text-blue-900 underline underline-offset-2"
                                    >
                                        Annulla modifica
                                    </button>
                                </div>
                            </div>
                        )}

                        {!isEditingExisting && (
                            <div className="bg-blue-50 text-blue-800 p-4 rounded-xl flex gap-3 text-sm font-medium border border-blue-200">
                                <Info size={20} className="shrink-0 text-blue-500 mt-0.5" />
                                <p>Compila km percorsi, eventuali ore extra e spese sostenute. Le note sono facoltative. Tutto viene inviato in approvazione in un'unica trasmissione.</p>
                            </div>
                        )}

                        {/* Gestione tempi come in ReportTab, senza orari indagine: per i turni
                            vengono dallo start/stop. */}
                        <div>
                            <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3 px-1">Gestione Tempi</h3>

                            <div className="grid grid-cols-3 gap-3">
                                <div className="bg-emerald-50 border border-emerald-100 p-4 rounded-2xl flex flex-col justify-center shadow-sm">
                                    <label className="text-[10px] font-bold text-emerald-600/70 uppercase tracking-wide mb-1 flex items-center gap-1">
                                        <Activity size={12} /> Ore Registrate
                                    </label>
                                    <div className="text-2xl font-black text-emerald-700">
                                        {oreGps}<span className="text-sm font-semibold text-emerald-600/50">h</span>
                                    </div>
                                    <span className="text-[8px] text-emerald-600 font-medium uppercase mt-1">Calcolate in automatico</span>
                                </div>
                                <div className="bg-white border border-slate-200 p-3 rounded-2xl shadow-sm focus-within:ring-2 ring-blue-100">
                                    <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wide block mb-1">Ore Extra (Ufficio)</label>
                                    <NumberInput
                                        min={0} step="0.5" className="w-full bg-transparent font-semibold text-lg text-slate-800 outline-none"
                                        value={oreExtra} onChange={e => setOreExtra(e.target.value)} placeholder="0.0" />
                                </div>
                                <div className="bg-white border border-slate-200 p-3 rounded-2xl shadow-sm focus-within:ring-2 ring-blue-100">
                                    <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wide block mb-1">Km Percorsi</label>
                                    <NumberInput
                                        min={0} className="w-full bg-transparent font-semibold text-lg text-slate-800 outline-none"
                                        value={kmFatti} onChange={e => setKmFatti(e.target.value)} placeholder="0" />
                                    {parseDecimal(kmFatti) > 0 && (
                                        <AutoAziendaleSelector className="mt-2" value={autoAziendale} onChange={setAutoAziendale} />
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Note di fine turno, facoltative */}
                        <div>
                            <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2 px-1">Note Fine Turno (facoltativo)</label>
                            <div className="bg-white border border-slate-200 rounded-2xl shadow-sm focus-within:ring-2 focus-within:ring-blue-500 transition-all">
                                <textarea
                                    className="w-full p-4 bg-transparent border-0 text-sm font-medium text-slate-700 placeholder-slate-400 outline-none min-h-[90px] resize-y"
                                    placeholder="Eventuali segnalazioni, anomalie o consegne..."
                                    value={noteFineTurno} onChange={e => setNoteFineTurno(e.target.value)} />
                            </div>
                        </div>

                        {/* Spese solo nel primo invio: /modifica le ignora */}
                        {!isEditingExisting && (
                        <div className="pt-2 border-t border-slate-200">
                            <div className="flex items-center justify-between mt-6 mb-3 px-1">
                                <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider flex items-center gap-1.5">
                                    <Receipt size={14} /> Spese e Scontrini
                                </h3>
                                <button type="button" onClick={addSpesaRow}
                                        className="text-xs font-bold text-blue-600 bg-blue-50 px-3 py-1.5 rounded-lg flex items-center gap-1 hover:bg-blue-100 transition-colors">
                                    <Plus size={14}/> Aggiungi
                                </button>
                            </div>

                            <div className="space-y-4">
                                {listaSpese.length === 0 && (
                                    <div className="text-center py-6 border border-dashed border-slate-200 rounded-xl text-slate-400 text-sm">Nessuna spesa extra da rendicontare.</div>
                                )}
                                {listaSpese.map((s, i) => (
                                    <div key={i} className="flex flex-col gap-4 p-5 bg-white border border-slate-200 rounded-xl shadow-sm animate-in fade-in relative group">
                                        <button type="button" onClick={() => removeSpesaRow(i)} className="absolute -top-2 -right-2 bg-white border border-slate-200 text-slate-400 hover:text-rose-500 rounded-full p-1.5 shadow-sm opacity-0 group-hover:opacity-100 transition-opacity">
                                            <X size={16} />
                                        </button>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <div>
                                                <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">Categoria *</label>
                                                <select required className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-lg text-sm font-semibold focus:ring-2 ring-blue-500 outline-none" value={s.tipo} onChange={e => updateSpesa(i, 'tipo', e.target.value)}>
                                                    <option value="">-- Seleziona --</option>
                                                    <option value="Pasti">Pasti</option>
                                                    <option value="Trasporti">Trasporti</option>
                                                    <option value="Carburante">Carburante</option>
                                                    <option value="Parcheggio">Parcheggio</option>
                                                    <option value="Alloggio">Alloggio</option>
                                                    <option value="Altro">Altro</option>
                                                </select>
                                            </div>
                                            <div>
                                                <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">Importo (€) *</label>
                                                <div className="relative">
                                                    <span className="absolute left-3 top-2.5 text-slate-400 font-bold">€</span>
                                                    <NumberInput
                                                        min={0} step="0.01" required className="w-full p-2.5 pl-8 bg-slate-50 border border-slate-200 rounded-lg text-sm font-bold focus:ring-2 ring-blue-500 outline-none"
                                                        value={s.importo} onChange={e => updateSpesa(i, 'importo', e.target.value)} placeholder="0.00" />
                                                </div>
                                            </div>
                                        </div>
                                        <div className="flex flex-col md:flex-row gap-4 items-start">
                                            <div className="flex-1 w-full">
                                                <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">Note Brevi</label>
                                                <input type="text" placeholder="Es. Pranzo, Parcheggio, ecc..." className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:ring-2 ring-blue-500 outline-none" value={s.note} onChange={e => updateSpesa(i, 'note', e.target.value)} />
                                            </div>
                                            <div className="w-full md:w-64 bg-purple-50 border border-purple-200 p-2.5 rounded-lg flex items-center gap-3 mt-0 md:mt-5 shrink-0">
                                                <input type="checkbox" id={`turno-carta-${i}`} className="w-4 h-4 text-purple-600 rounded cursor-pointer" checked={s.anticipatoAzienda} onChange={e => updateSpesa(i, 'anticipatoAzienda', e.target.checked)} />
                                                <label htmlFor={`turno-carta-${i}`} className="text-[10px] font-bold text-purple-900 cursor-pointer leading-tight">
                                                    CARTA AZIENDALE <br /><span className="font-normal opacity-80">(Nessun rimborso)</span>
                                                </label>
                                            </div>
                                            <label className={`w-full md:w-32 flex items-center justify-center p-3 border rounded-lg transition-all cursor-pointer mt-0 md:mt-5 ${s.file ? 'bg-emerald-50 text-emerald-600 border-emerald-200' : 'bg-slate-50 border-slate-200 text-slate-500 hover:bg-slate-100'}`}>
                                                <Camera size={16} className="mr-1.5" />
                                                <span className="text-[10px] font-bold uppercase tracking-wider">{s.file ? 'FOTO OK' : 'SCONTRINO'}</span>
                                                <input type="file" capture="environment" accept="image/*" className="hidden" onChange={e => { if (e.target.files) updateSpesa(i, 'file', e.target.files[0]); }} />
                                            </label>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                        )}

                        <button
                            onClick={handleSendReport}
                            disabled={isSavingReport}
                            className={`w-full text-white py-4 rounded-xl font-semibold text-lg shadow-lg transition-all active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 ${isEditingExisting ? 'bg-amber-600 hover:bg-amber-700' : 'bg-emerald-600 hover:bg-emerald-700'}`}
                        >
                            {isSavingReport ? <Loader2 className="animate-spin" size={20}/> : (isEditingExisting ? <RotateCcw size={20}/> : <Send size={20}/>)}
                            {isSavingReport
                                ? (isEditingExisting ? 'Re-invio in corso...' : 'Invio in corso...')
                                : (isEditingExisting ? 'Re-invia al Titolare' : 'Invia in Approvazione')}
                        </button>
                    </div>
                )}

            </div>

            {/* Modale nota operativa (stessa NoteModal degli incarichi) */}
            {isNoteModalOpen && (
                <NoteModal
                    noteText={noteText}
                    setNoteText={setNoteText}
                    actionLoading={savingNote}
                    onSave={handleSaveNote}
                    onClose={() => { setIsNoteModalOpen(false); setNoteText(''); }}
                />
            )}
        </div>
    );
};

export default TurnoOperativoDashboard
